import React, { useState } from 'react';
import axios from 'axios';

const TasksCard = ({ tasks, setTasks, user, adminUsers, taskStats, API_BASE }) => {
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    assigned_to: '',
    priority: 'medium',
    task_date: new Date().toISOString().split('T')[0]
  });

  // Get user's tasks, sorted by priority and date
  const userTasks = tasks
    .filter(task => task.assigned_to === user?.id && task.status !== 'completed')
    .sort((a, b) => {
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    })
    .slice(0, 5);

  const handleToggleTask = async (taskId, currentStatus) => {
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    try {
      const response = await axios.put(
        `${API_BASE}/calendar/tasks/${taskId}`,
        { status: newStatus },
        { withCredentials: true }
      );
      setTasks(tasks.map(task => task.id === taskId ? response.data : task));
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleCreateTask = async () => {
    if (!taskForm.title.trim()) {
      alert('Task title is required');
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE}/calendar/tasks`,
        taskForm,
        { withCredentials: true }
      );
      setTasks([...tasks, response.data]);
      setTaskForm({
        title: '',
        description: '',
        assigned_to: '',
        priority: 'medium',
        task_date: new Date().toISOString().split('T')[0]
      });
      setShowTaskModal(false);
    } catch (error) {
      console.error('Error creating task:', error);
      alert('Failed to create task');
    }
  };

  return (
    <div className="desk-card tasks-card">
      <div className="desk-card-header">
        <div className="desk-card-header-left">
          <div className="desk-card-icon tasks">
            <span>✓</span>
          </div>
          <span className="desk-card-title">My Tasks</span>
        </div>
        <button className="desk-card-action" onClick={() => setShowTaskModal(true)}>
          + New Task
        </button>
      </div>
      <div className="desk-card-body">
        {/* Stats Row */}
        <div className="stats-row">
          <div className="stat-box">
            <span className="number blue">{taskStats.assignedCount}</span>
            <span className="label">Assigned</span>
          </div>
          <div className="stat-box">
            <span className="number green">{taskStats.completedCount}</span>
            <span className="label">Completed</span>
          </div>
          <div className="stat-box">
            <span className="number orange">{taskStats.overdueCount}</span>
            <span className="label">Overdue</span>
          </div>
        </div>

        {/* Task List */}
        <div className="task-list">
          {userTasks.length === 0 ? (
            <div className="empty-state" style={{ padding: '1rem', textAlign: 'center', color: '#a0aec0' }}>
              <p>No tasks assigned to you</p>
            </div>
          ) : (
            userTasks.map(task => (
              <div
                key={task.id}
                className={`task-item ${task.status === 'completed' ? 'completed' : ''}`}
              >
                <div
                  className={`task-checkbox ${task.status === 'completed' ? 'checked' : ''}`}
                  onClick={() => handleToggleTask(task.id, task.status)}
                >
                  {task.status === 'completed' && '✓'}
                </div>
                <div className="task-content">
                  <div className="task-title">{task.title}</div>
                  <div className="task-meta">
                    Due: {new Date(task.task_date).toLocaleDateString()}
                  </div>
                </div>
                <span className={`task-priority ${task.priority}`}>
                  {task.priority}
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Task Creation Modal */}
      {showTaskModal && (
        <div className="modal-overlay" onClick={() => setShowTaskModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create New Task</h3>
              <button className="modal-close" onClick={() => setShowTaskModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Task Title *</label>
                <input
                  type="text"
                  value={taskForm.title}
                  onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                  placeholder="Enter task title"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={taskForm.description}
                  onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                  placeholder="Enter task description"
                  rows="3"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Due Date</label>
                  <input
                    type="date"
                    value={taskForm.task_date}
                    onChange={(e) => setTaskForm({ ...taskForm, task_date: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Priority</label>
                  <select
                    value={taskForm.priority}
                    onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Assign To</label>
                <select
                  value={taskForm.assigned_to}
                  onChange={(e) => setTaskForm({ ...taskForm, assigned_to: e.target.value })}
                >
                  <option value="">Select a user</option>
                  {adminUsers.map((admin) => (
                    <option key={admin.id} value={admin.id}>
                      {admin.name || `User ${admin.id}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowTaskModal(false)}>Cancel</button>
              <button className="btn-create" onClick={handleCreateTask}>Create Task</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TasksCard;
